import { z } from 'zod';

import type { ExcelError, ExcelResult } from '@apple/utils/files/models/excel';
import { createExcelErrorSchema, createExcelResultSchema } from '@apple/utils/files/models/excel';

/** @see AppleRetail.Web.Features.Vintage.VintageManagementController.VintageListingModel */
export interface VintageListing {
	vintageId: number;
	name: string;
	description: string | null;
	storesAssigned: number;
	productsTagged: number;
}

export const vintageListingSchema = z.object({
	vintageId: z.number(),
	name: z.string(),
	description: z.string().nullable(),
	storesAssigned: z.number(),
	productsTagged: z.number(),
}) satisfies z.ZodType<VintageListing>;

/** @see AppleRetail.Web.Features.Vintage.VintageManagementController.VintageModel */
export interface Vintage {
	vintageId: number;
	name: string;
	description: string | null;
}

export const vintageSchema = z.object({
	vintageId: z.number(),
	name: z.string(),
	description: z.string().nullable(),
}) satisfies z.ZodType<Vintage>;

/** @see AppleRetail.Web.Features.Vintage.VintageManagementController.VintageStoreModel */
export interface VintageStoreRow {
	arvatoCustomerCode: string;
	storeId: string;
	name: string;
	country: string | null;
	plant: string | null;
}

export const vintageStoreRowSchema = z.object({
	arvatoCustomerCode: z.string(),
	storeId: z.string(),
	name: z.string(),
	country: z.string().nullable(),
	plant: z.string().nullable(),
}) satisfies z.ZodType<VintageStoreRow>;

/** @see AppleRetail.Web.Features.Vintage.VintageManagementController.VintageProductModel */
export interface VintageProductRow {
	itemId: string;
	partNumber: string | null;
	name: string;
	plant: string | null;
}

export type VintageExcelResult = ExcelResult<
	VintageBulkUploadOperation,
	'id',
	VintageExcelResultError
>;

export type VintageExcelResultError = ExcelError<VintageBulkUploadOperation, 'id'>;
export interface VintageBulkUploadOperation {
	[key: string]: unknown;
	id: string;
	type: 'Vintage' | 'Store' | 'Product';
	vintageId?: number | null;
	storeId?: string | null;
	currentVintageName?: string | null;
	itemId?: string | null;
	productName?: string | null;
	vintageName: string;
	description?: string | null;
	originalItemId?: string | null;
}

export const vintageBulkUploadOperationSchema = z.object({
	id: z.string(),
	type: z.enum(['Vintage', 'Store', 'Product']),
	vintageId: z.number().nullable().optional(),
	storeId: z.string().nullable().optional(),
	currentVintageName: z.string().nullable().optional(),
	itemId: z.string().nullable().optional(),
	productName: z.string().nullable().optional(),
	vintageName: z.string(),
	description: z.string().nullable().optional(),
	originalItemId: z.string().nullable().optional(),
}) satisfies z.ZodType<VintageBulkUploadOperation>;

export const vintageExcelErrorSchema = createExcelErrorSchema(
	z.object({ id: z.string() }),
) satisfies z.ZodType<VintageExcelResultError>;

export const vintageExcelResultSchema = createExcelResultSchema(
	vintageBulkUploadOperationSchema,
	vintageExcelErrorSchema,
	z.string(),
) satisfies z.ZodType<VintageExcelResult>;

export const vintageProductRowSchema = z.object({
	itemId: z.string(),
	partNumber: z.string().nullable(),
	name: z.string(),
	plant: z.string().nullable(),
}) satisfies z.ZodType<VintageProductRow>;

/** @see AppleRetail.Web.Features.Vintage.VintageManagementController.VerifyStoresResponse */
export interface VerifyStoresResponse {
	valid: VintageStoreRow[];
	invalid: string[];
}

export const verifyStoresResponseSchema = z.object({
	valid: z.array(vintageStoreRowSchema),
	invalid: z.array(z.string()),
}) satisfies z.ZodType<VerifyStoresResponse>;

/** @see AppleRetail.Web.Features.Vintage.VintageManagementController.VintageSaveModel */
export interface VintageSaveModel {
	vintageId: number;
	name: string;
	description: string | null;
	isNewVintage: boolean;
	addedStoreCodes: string[];
	removedStoreCodes: string[];
	addedItemIds: string[];
	removedItemIds: string[];
}
